import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useFormContext } from "react-hook-form";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { toast } from "@/utils/toast";
import { validateFile } from "@/features/company/utiles/file";
import { black } from "@/theme/colors";
import { Modal } from "@/components/elements/Modal";
import { useDialog } from "@/hooks/useDialog";
import { Button } from "@/components/elements/Button";
import { amber, blue, deepOrange, deepPurple, green, pink } from "@mui/material/colors";
import { useParams } from "react-router-dom";
import { updateLogoApi } from "@/api/services/main/company";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";

const colors = [deepOrange[500], deepPurple[500], blue[500], green[500], pink[500], amber[500]];

export default function UploadLogo() {
  const { companyId } = useParams();
  const { watch, setValue } = useFormContext<TCompanyForm>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [logo, setLogo] = useState<string | null>(null);

  const confirmModal = useDialog();
  const confirmClearImageModal = useDialog();

  const companyName = watch("companyName");

  const getRandomColor = () => {
    const index = Math.floor(Math.random() * colors.length);
    return colors[index];
  };
  const [bgColor, setBgColor] = useState<string>(() => {
    return getRandomColor();
  });

  const companyLogo = watch("companyLogo");
  useEffect(() => {
    if (companyLogo) {
      setLogo(companyLogo);
      setBgColor("");
    }
  }, [companyLogo]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCloseModal = () => confirmModal.cancel();

  const handleShowModalUpdate = () => confirmModal.show();

  const handleClearImage = async () => {
    const confirmClear = await confirmClearImageModal.show();
    if (confirmClear) {
      setLogo(null);
      setValue("companyLogoFile", null);
      setValue("companyLogo", "");
      setBgColor(getRandomColor());
      if (companyId) {
        await updateLogoApi(companyId, null);
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    const error = validateFile(file);
    if (error) {
      toast.error(error);
      event.target.value = "";
      return;
    }

    setValue("companyLogoFile", file, { shouldValidate: true, shouldDirty: true });
    setBgColor("");

    setLogo(URL.createObjectURL(file));

    if (companyId) {
      await updateLogoApi(companyId, file);
    }
    event.target.value = "";
  };

  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: 1,
        bgcolor: `${bgColor}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        cursor: "pointer",
      }}
    >
      {logo ? (
        <Box
          component="img"
          src={logo}
          alt="ロゴ"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: 2,
          }}
        />
      ) : (
        <Typography variant="body2" color={black[60]} fontSize={24}>
          {companyName ? companyName.charAt(0) : ""}
        </Typography>
      )}

      <IconButton
        size="small"
        sx={{
          position: "absolute",
          bottom: -5,
          right: -5,
          bgcolor: "white",
          boxShadow: 1,
          "&:hover": { bgcolor: "grey.100" },
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleShowModalUpdate();
        }}
      >
        <CameraAltIcon fontSize="small" />
      </IconButton>

      <input type="file" accept=".jpg,.jpeg,.png" hidden ref={fileInputRef} onChange={handleFileChange} />
      <Modal isOpen={confirmModal.open} onClose={handleCloseModal} width={360} title={"プロフィール画像の変更"} enableFooter={false}>
        <Stack alignItems={"center"}>
          <Box
            sx={{
              width: 188,
              height: 188,
              marginTop: "24px",
              marginBottom: "24px",
              borderRadius: 1,
              bgcolor: `${bgColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              cursor: "pointer",
            }}
          >
            {logo ? (
              <Box
                component="img"
                src={logo}
                alt="ロゴ"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: 2,
                }}
              />
            ) : (
              <Typography variant="body2" color={black[60]} fontSize={94}>
                {companyName ? companyName.charAt(0) : ""}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            {logo && (
              <Button variant="danger" size="sm" onClick={handleClearImage}>
                画像を削除
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleUploadClick}>
              画像をアップロード
            </Button>
          </Stack>
        </Stack>
      </Modal>
      <ConfirmSaveModal
        title="削除の確認"
        open={confirmClearImageModal.open}
        onOk={confirmClearImageModal.confirm}
        onClose={confirmClearImageModal.cancel}
      >
        <Typography component="p" variant="subtitle1">
          本当に画像を削除しますか？
          <br />
          削除後、デフォルトのアバターに切り替わります。
        </Typography>
      </ConfirmSaveModal>
    </Box>
  );
}
